import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../../utils/api';

// Smart URL builder to prevent double /api/api/
const RAW_API_BASE = (process.env.REACT_APP_API_URL || 'https://localhost:7018').replace(/\/$/, '');
const API_BASE = RAW_API_BASE.endsWith('/api') ? RAW_API_BASE : `${RAW_API_BASE}/api`;

const CATEGORY_IMAGES = {
  'Corporate': 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&h=300&fit=crop',
  'SME': 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=400&h=300&fit=crop',
  'Family': 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=400&h=300&fit=crop',
  'Retail (Individual)': 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=300&fit=crop',
  'Senior': 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop',
  'Elderly Care': 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=300&fit=crop',
  'Micro': 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop',
  'Beta': 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop'
};

function PlanCategories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPlanCategories();
  }, []);

  const fetchPlanCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const endpoint = `${API_BASE}/PlanCategories/plan-categories`;
      const response = await authenticatedFetch(endpoint, 'POST', {});
      
      if (response?.status === 'success' || response?.success === true) {
        // Filter only active categories
        const activeCategories = (response.data || []).filter(cat => cat.isActive);
        setCategories(activeCategories);
      } else {
        throw new Error(response?.message || 'Failed to fetch categories');
      }
    } catch (err) {
      console.error('💥 Error fetching plan categories:', err);
      setError(err.message || 'An error occurred while fetching categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryId, categoryName) => {
    navigate(`/allplans?categoryId=${categoryId}&categoryName=${encodeURIComponent(categoryName)}`);
  };

  if (loading) {
    return (
      <section className="py-5" style={{ backgroundColor: '#fafafa' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3" style={{ color: '#1f2937' }}>Our Plan Categories</h2>
            <p className="text-muted">Choose the perfect health plan category for your needs</p>
          </div>
          <div className="row g-4">
            {[1, 2, 3, 4].map(i => (
              <div className="col-md-6 col-lg-3" key={i}>
                <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                  <div className="skeleton" style={{ height: '200px' }}></div>
                  <div className="card-body p-3">
                    <div className="skeleton" style={{ height: '20px', width: '80%', marginBottom: '10px', borderRadius: '4px' }}></div>
                    <div className="skeleton" style={{ height: '36px', borderRadius: '6px' }}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-5" style={{ backgroundColor: '#fafafa' }}>
        <div className="container">
          <div className="alert alert-warning text-center" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>{error}
            <button className="btn btn-sm btn-outline-warning ms-3" onClick={fetchPlanCategories}>
              <i className="fas fa-redo me-1"></i>Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .skeleton { 
          animation: pulse 1.5s infinite; 
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); 
          background-size: 200% 100%; 
        } 
        @keyframes pulse { 
          0%, 100% { opacity: 1; } 
          50% { opacity: 0.5; } 
        }
      `}</style>

      <section className="py-5" style={{ backgroundColor: '#fafafa' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3" style={{ color: '#1f2937' }}>Our Plan Categories</h2>
            <p className="text-muted">Choose the perfect health plan category for your needs</p>
          </div>

          <div className="row g-4">
            {categories.map((category, index) => {
              // ✅ Backend now provides the FULL URL in category.image, so we use it directly!
              const fallbackUrl = CATEGORY_IMAGES[category.categoryName] || 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=300&fit=crop';
              const imageUrl = category.image ? category.image : fallbackUrl;
              
              return (
                <div className="col-md-6 col-lg-3" key={category.id || category.planCategoryUid}>
                  <div 
                    className="card h-100 border-0 shadow-sm"
                    style={{ 
                      borderRadius: '16px', 
                      overflow: 'hidden', 
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease', 
                      cursor: 'pointer', 
                      background: 'white',
                      height: '100%',
                      animation: `${index % 2 === 0 ? 'slideInLeft' : 'slideInRight'} 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${0.1 * index}s forwards`
                    }}
                    onClick={() => handleCategoryClick(category.id || category.planCategoryUid, category.categoryName)}
                    onMouseOver={(e) => { 
                      e.currentTarget.style.transform = 'translateY(-8px)'; 
                      e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'; 
                    }}
                    onMouseOut={(e) => { 
                      e.currentTarget.style.transform = 'translateY(0)'; 
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'; 
                    }}
                  >
                    <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
                      <img 
                        src={imageUrl} 
                        alt={category.categoryName} 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease'
                        }}
                        // ✅ Graceful fallback if the uploaded image URL is broken or missing on the server
                        onError={(e) => {
                          e.target.onerror = null; // Prevent infinite error loop
                          e.target.src = fallbackUrl;
                        }}
                      />
                    </div>
                    <div className="card-body p-3">
                      <h5 className="card-title mb-3" style={{ 
                        fontWeight: '700', 
                        fontSize: '1.1rem', 
                        color: '#1f2937', 
                        minHeight: '50px', 
                        display: 'flex', 
                        alignItems: 'center' 
                      }}>
                        {category.categoryName}
                      </h5>
                      <button 
                        className="btn w-100" 
                        style={{ 
                          backgroundColor: '#e88c38', 
                          color: 'white', 
                          fontWeight: '600', 
                          borderRadius: '8px', 
                          padding: '10px 16px', 
                          transition: 'all 0.3s ease',
                          border: 'none'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#d67a2b';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(232, 140, 56, 0.3)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = '#e88c38';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handleCategoryClick(category.id || category.planCategoryUid, category.categoryName); 
                        }}
                      >
                        Learn More <i className="fas fa-arrow-right ms-1"></i>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

export default PlanCategories;